import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import styles from './ComparisonDialog.module.css';
import { compareValues } from '../../../../utils/comparisonUtils';

const ComparisonDialog = ({
    isOpen,
    onClose,
    fieldName,
    source1Data,
    source2Data,
    source3Data,
    source1Name = "OCR Data",
    source2Name = "MRZ/PDF417 Data",
    source3Name = "SoR Data",
    showThreeSources = false,
    mismatches = null //array of rows to render
}) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    // helper: returns class for a single cell based on other values in the row
    const getCellClassNameForRow = (value, row) => {
        const val = (value === undefined || value === null) ? '' : String(value);
        if (!val || val === '-') return '';

        // collect other values in the row excluding '-'
        const others = [];
        if (row.ocr && row.ocr !== '-' && row.ocr !== val) others.push(row.ocr);
        if (row.mrz && row.mrz !== '-' && row.mrz !== val) others.push(row.mrz);
        if (row.sor && row.sor !== '-' && row.sor !== val) others.push(row.sor);

        // If there are no other values to compare, do not color
        if (others.length === 0) return '';

        // If this value matches any other present value -> match (green), else mismatch (orange)
        const matchesAny = others.some(other => compareValues(val, other));
        return matchesAny ? styles.matchCell : styles.mismatchCell;
    };

    // Build rows: use mismatches if provided, otherwise fallback to single-field props
    const rows = (Array.isArray(mismatches) && mismatches.length > 0) ? mismatches : [
        {
            attribute: fieldName,
            ocr: source1Data ?? '-',
            mrz: source2Data ?? '-',
            sor: showThreeSources ? (source3Data ?? '-') : '-'
        }
    ];

    const renderComparisonTable = () => {
        return (
            <table className={styles.comparisonTable}>
                <thead className={styles.tableHeader}>
                    <tr>
                        <th>Attribute</th>
                        <th>{source1Name || 'OCR Data'}</th>
                        <th>{source2Name || 'MRZ/PDF417 Data'}</th>
                        <th>{source3Name || 'SoR Data'}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, index) => (
                        <tr key={index} className={styles.tableRow}>
                            <td className={styles.attributeCell}>{row.attribute}</td>
                            <td className={`${styles.valueCell} ${getCellClassNameForRow(row.ocr, row)}`}>
                                {row.ocr ?? '-'}
                            </td>
                            <td className={`${styles.valueCell} ${getCellClassNameForRow(row.ocr, row)}`}>
                                {row.mrz ?? '-'}
                            </td>
                            <td className={`${styles.valueCell} ${source3Data ? getCellClassNameForRow(row.ocr, row) : ''}`}>
                                {row.sor ?? '-'}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    return (
        <Dialog
            fullScreen={fullScreen}
            open={isOpen}
            onClose={onClose}
            aria-labelledby="comparison-dialog-title"
            maxWidth="md"
            fullWidth
            sx={{
                "& .MuiPaper-root": {
                    borderRadius: '12px',
                    padding: '8px'
                }
            }}
        >
            <DialogTitle id="comparison-dialog-title" className={styles.dialogTitle}>
                <div className={styles.dialogHeader}>
                    <div>
                        <Typography variant="h6" component="div">
                            Applicant Data Does Not Match
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Please note that the information parsed from the primary document does not match.
                        </Typography>
                    </div>
                    <IconButton
                        onClick={onClose}
                        className={styles.closeIcon}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                </div>
            </DialogTitle>

            <DialogContent className={styles.dialogContent}>
                {renderComparisonTable()}
            </DialogContent>
        </Dialog>
    );
};

export default ComparisonDialog;